/**
 * useTheme Hook —— 主题管理
 *
 * 【什么是主题？】
 * 在 Web 应用中，"主题"指的是视觉样式方案。最常见的主题切换就是"亮色/暗色"模式。
 * 主题通常包括：颜色方案、字体大小、间距等视觉变量。
 *
 * 【这个 Hook 管理什么？】
 * - mode: 亮色(light) 还是 暗色(dark)
 * - colorPresets: 颜色预设方案（默认、青色、紫色、蓝色等）
 * - layout: 布局方式（垂直/水平/迷你）
 * - compact: 是否使用紧凑模式
 *
 * 【主题切换的原理】
 * 1. React 状态管理：用 useState 保存当前主题配置
 * 2. DOM 同步：通过 data-theme 等属性将主题应用到 HTML 根元素
 * 3. CSS 变量：CSS 通过 var(--xxx) 读取这些属性对应的变量值
 * 4. 持久化：主题配置保存到 localStorage，刷新后恢复
 *
 * 【为什么把主题保存到 localStorage？】
 * 用户选择了暗色主题后，刷新页面不应该回到亮色主题。
 * localStorage 让主题选择在页面刷新、甚至关闭浏览器后仍然保持。
 */

import { ThemeColorPresets, ThemeMode } from "@zero/types";
import { useCallback, useEffect, useState } from "react";

/**
 * 主题配置接口
 *
 * 描述了一个完整的主题配置包含哪些选项。
 */
export interface ThemeConfig {
  mode: ThemeMode; // 亮色/暗色模式
  colorPresets: ThemeColorPresets; // 颜色预设
  layout?: "vertical" | "horizontal" | "mini"; // 布局方式（可选）
  compact?: boolean; // 紧凑模式（可选）
}

/**
 * useTheme Hook
 *
 * @param defaultConfig - 默认主题配置（部分可选）
 * @returns 主题状态和修改方法
 *
 * @example
 * const { mode, toggleMode } = useTheme();
 * // mode === 'light' 或 'dark'
 * // toggleMode() 切换亮暗
 *
 * 【useState 的惰性初始化函数】
 * useState(() => {...}) 中的函数只在首次渲染时执行一次。
 * 用于从 localStorage 恢复主题或使用默认值。
 */
export function useTheme(defaultConfig?: Partial<ThemeConfig>) {
  // 初始化主题状态
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    // SSR 安全检查
    if (typeof window === "undefined") {
      return {
        mode: ThemeMode.Light,
        colorPresets: ThemeColorPresets.Default,
      };
    }

    try {
      // 尝试从 localStorage 恢复之前保存的主题
      const stored = window.localStorage.getItem("theme");
      if (stored) {
        return JSON.parse(stored) as ThemeConfig;
      }
    } catch {
      // JSON 解析失败，使用默认值
    }

    // 没有保存的主题，使用默认配置
    // ?? 是空值合并运算符：左侧为 null/undefined 时使用右侧的值
    return {
      mode: defaultConfig?.mode ?? ThemeMode.Light,
      colorPresets: defaultConfig?.colorPresets ?? ThemeColorPresets.Default,
      layout: defaultConfig?.layout,
      compact: defaultConfig?.compact,
    };
  });

  /**
   * 同步主题到 DOM
   *
   * 【useEffect 的作用】
   * 当 theme.mode 或 theme.colorPresets 变化时，
   * 自动更新 HTML 根元素的 data-* 属性。
   *
   * CSS 中可以这样使用：
   * [data-theme="dark"] { --bg-color: #1a1a1a; }
   * [data-theme="light"] { --bg-color: #ffffff; }
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // 设置 data-theme 属性（亮/暗模式）
    document.documentElement.setAttribute("data-theme", theme.mode);
    // 设置 data-color-preset 属性（颜色预设）
    if (theme.colorPresets) {
      document.documentElement.setAttribute("data-color-preset", theme.colorPresets);
    }
  }, [theme.mode, theme.colorPresets]);

  /**
   * 设置亮暗模式
   *
   * 【不可变更新模式】
   * setThemeState((prev) => { ... }) 中：
   * - prev 是当前状态（旧值）
   * - {...prev, mode} 创建一个新对象，复制 prev 的所有字段，然后覆盖 mode
   * - 原始的 prev 对象不会被修改（不可变性）
   *
   * 为什么不可变？因为 React 通过"引用比较"判断状态是否变化。
   * 如果直接修改旧对象，React 检测不到变化，组件不会重新渲染。
   */
  const setMode = useCallback((mode: ThemeMode): void => {
    setThemeState((prev) => {
      const newTheme = { ...prev, mode };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", JSON.stringify(newTheme));
      }
      return newTheme;
    });
  }, []);

  /** 设置颜色预设 */
  const setColorPresets = useCallback((colorPresets: ThemeColorPresets): void => {
    setThemeState((prev) => {
      const newTheme = { ...prev, colorPresets };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", JSON.stringify(newTheme));
      }
      return newTheme;
    });
  }, []);

  /** 切换亮暗模式 */
  const toggleMode = useCallback((): void => {
    setThemeState((prev) => {
      // 三元运算符：如果是亮色则切换为暗色，否则切换为亮色
      const newMode: ThemeMode = prev.mode === ThemeMode.Light ? ThemeMode.Dark : ThemeMode.Light;
      const newTheme = { ...prev, mode: newMode };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", JSON.stringify(newTheme));
      }
      return newTheme;
    });
  }, []);

  /** 设置布局方式 */
  const setLayout = useCallback((layout: "vertical" | "horizontal" | "mini"): void => {
    setThemeState((prev) => {
      const newTheme = { ...prev, layout };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", JSON.stringify(newTheme));
      }
      return newTheme;
    });
  }, []);

  /** 设置紧凑模式 */
  const setCompact = useCallback((compact: boolean): void => {
    setThemeState((prev) => {
      const newTheme = { ...prev, compact };
      if (typeof window !== "undefined") {
        window.localStorage.setItem("theme", JSON.stringify(newTheme));
      }
      return newTheme;
    });
  }, []);

  return {
    mode: theme.mode,
    colorPresets: theme.colorPresets,
    layout: theme.layout,
    compact: theme.compact,
    setMode,
    setColorPresets,
    setLayout,
    setCompact,
    toggleMode,
  };
}

/**
 * 获取系统主题偏好
 *
 * 【prefers-color-scheme 是什么？】
 * 这是浏览器提供的媒体查询，可以检测操作系统的亮/暗色设置。
 * 如果用户在系统设置中选择了暗色模式，这里返回 ThemeMode.Dark。
 *
 * 注意：这个函数不是 Hook（没有 use 前缀），不会触发响应式更新。
 * 如果需要响应式检测，请使用 useMediaQuery({ prefersColorScheme: 'dark' })。
 */
export function useSystemTheme(): ThemeMode {
  if (typeof window === "undefined") {
    return ThemeMode.Light;
  }

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  return mediaQuery.matches ? ThemeMode.Dark : ThemeMode.Light;
}
